import { useMediaQuery } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../../../App.styles';
import { SoldProductDetailsResponse } from '../../../../../hooks';
import { Typography } from '../../../../../stories/atoms';
import { ColumnFlexContainer } from '../../../../../stories/atoms/containerV2/ContainerV2';
import { ContainerDrawer } from '../../../../../stories/molecules/container-drawer/ContainerDrawer';
import { HeaderRowItem, Table } from '../../../../../stories/molecules/tablev2/Table';
import { muiTheme } from '../../../../../theme';
import { storeOverviewTranslations } from '../../../../../translations';
import { formatAmountWithSeparator } from '../../../../../utils';
import { StoreWidgetContent } from '../../../../storeOverviewPage/storeWidgetContent/StoreWidgetContent';
import { HeaderTooltip } from '../../headerTooltip/HeaderTooltip';
import { StyledIndexTypography } from '../mobileChipLayout/MobileChipLayout.styles';
import { NoDataForPeriodView } from '../noDataForPeriodView/NoDataForPeriodView';
import { ProductSummaryCard, ProductSummarySlider } from '../productSummaryCard/ProductSummaryCard';
import { getDifferenceText, getRankStatus, SoldProductItemRankedAreaSalesMarket } from '../sales.util';
import { getWeeklyTop20ProductsData } from './util';

enum TopSoldProductColumnType {
  SUBINDEX = 'subIndex',
  DEPARTMENT = 'department',
  PRODUCT = 'product',
  NETSALES = 'netSales',
  PIECES = 'pieces',
  PRICE = 'price',
  DIFFERENCELASTLASTWEEK = 'differenceLastLastWeek',
  RANKINGVSAREA = 'rankingVsArea',
  RANKINGVSSALESMARKET = 'rankingVsSalesMarket',
}

type TopSoldProductProps = {
  selectedFilter: string | undefined;
  weeklyTopSoldData: SoldProductDetailsResponse | null;
  isLoading: boolean;
  isError: boolean;
  retry?: () => void;
};

export const TopSoldProductWidget: FC<TopSoldProductProps> = ({
  selectedFilter,
  weeklyTopSoldData,
  isLoading,
  isError,
  retry,
}) => {
  const { t } = useTranslation();
  const {
    topSoldProducts: topSoldProductsTranslations,
    productTableColumn: productTableColumnTranslations,
    headerTooltips: headerTooltipsTranslations,
  } = storeOverviewTranslations;

  const [isSliderOpen, setIsSliderOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<SoldProductItemRankedAreaSalesMarket | null>(null);

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));

  const productData = useMemo(
    () => (weeklyTopSoldData ? getWeeklyTop20ProductsData(weeklyTopSoldData, selectedFilter) : null),
    [weeklyTopSoldData, selectedFilter],
  );

  const headerItems: HeaderRowItem<TopSoldProductColumnType>[] = [
    {
      id: TopSoldProductColumnType.SUBINDEX,
      width: '15%',
      content: (
        <Typography type="body3">
          {t(productTableColumnTranslations.subIndex.key, productTableColumnTranslations.subIndex.defaultValue)}
        </Typography>
      ),
    },
    {
      id: TopSoldProductColumnType.DEPARTMENT,
      width: '10%',
      content: (
        <Typography type="body3">
          {t(
            productTableColumnTranslations.department.key,
            productTableColumnTranslations.department.defaultValue,
          )}
        </Typography>
      ),
    },
    {
      id: TopSoldProductColumnType.PRODUCT,
      width: '20%',
      content: (
        <Typography type="body3">
          {t(productTableColumnTranslations.product.key, productTableColumnTranslations.product.defaultValue)}
        </Typography>
      ),
    },
    {
      id: TopSoldProductColumnType.NETSALES,
      width: '15%',
      content: (
        <HeaderTooltip
          headerText={t(
            productTableColumnTranslations.netSales.key,
            productTableColumnTranslations.netSales.defaultValue,
          )}
          tooltipText={t(headerTooltipsTranslations.netSale.key, headerTooltipsTranslations.netSale.defaultValue)}
        />
      ),
    },
    {
      id: TopSoldProductColumnType.PIECES,
      width: '8%',
      content: (
        <HeaderTooltip
          headerText={t(
            productTableColumnTranslations.pieces.key,
            productTableColumnTranslations.pieces.defaultValue,
          )}
          tooltipText={t(headerTooltipsTranslations.pieces.key, headerTooltipsTranslations.pieces.defaultValue)}
        />
      ),
    },
    {
      id: TopSoldProductColumnType.PRICE,
      width: '8%',
      content: (
        <HeaderTooltip
          headerText={t(
            productTableColumnTranslations.price.key,
            productTableColumnTranslations.price.defaultValue,
          )}
          tooltipText={t(headerTooltipsTranslations.price.key, headerTooltipsTranslations.price.defaultValue)}
        />
      ),
    },
    {
      id: TopSoldProductColumnType.DIFFERENCELASTLASTWEEK,
      width: '8%',
      content: (
        <HeaderTooltip
          headerText="Diff LLW"
          tooltipText={t(headerTooltipsTranslations.diffLLW.key, headerTooltipsTranslations.diffLLW.defaultValue)}
        />
      ),
    },
    {
      id: TopSoldProductColumnType.RANKINGVSAREA,
      width: '8%',
      content: (
        <HeaderTooltip
          headerText={t(
            productTableColumnTranslations.rankingVsArea.key,
            productTableColumnTranslations.rankingVsArea.defaultValue,
          )}
          tooltipText={t(
            headerTooltipsTranslations.rankingVsArea.key,
            headerTooltipsTranslations.rankingVsArea.defaultValue,
          )}
        />
      ),
    },
    {
      id: TopSoldProductColumnType.RANKINGVSSALESMARKET,
      width: '8%',
      content: (
        <HeaderTooltip
          headerText={t(
            productTableColumnTranslations.rankingVsSalesMarket.key,
            productTableColumnTranslations.rankingVsSalesMarket.defaultValue,
          )}
          tooltipText={t(
            headerTooltipsTranslations.rankingVsSM.key,
            headerTooltipsTranslations.rankingVsSM.defaultValue,
          )}
        />
      ),
    },
  ];

  const getTableRow = (item: SoldProductItemRankedAreaSalesMarket) => {
    const {
      subIndexDescription,
      departmentName,
      departmentNumber,
      productId,
      productName,
      netSale,
      soldPieces,
      price,
      differenceLastLastWeek,
      rankingVsArea,
      rankingVsSalesMarket,
    } = item;

    return [
      {
        id: TopSoldProductColumnType.SUBINDEX,
        content: <Typography type="body3">{subIndexDescription}</Typography>,
      },
      {
        id: TopSoldProductColumnType.DEPARTMENT,
        content: (
          <ColumnFlexContainer>
            <Typography type="body3">{departmentNumber}</Typography>
            <SecondaryTextTypography type="caption">{departmentName}</SecondaryTextTypography>
          </ColumnFlexContainer>
        ),
      },
      {
        id: TopSoldProductColumnType.PRODUCT,
        content: (
          <ColumnFlexContainer>
            <Typography type="body3">{productName}</Typography>
            <SecondaryTextTypography type="caption">{productId}</SecondaryTextTypography>
          </ColumnFlexContainer>
        ),
      },
      {
        id: TopSoldProductColumnType.NETSALES,
        content: <Typography>{formatAmountWithSeparator(netSale, null, 0)}</Typography>,
      },
      {
        id: TopSoldProductColumnType.PIECES,
        content: <Typography>{soldPieces}</Typography>,
      },
      {
        id: TopSoldProductColumnType.PRICE,
        content: <Typography>{formatAmountWithSeparator(price, null, 2)}</Typography>,
      },
      {
        id: TopSoldProductColumnType.DIFFERENCELASTLASTWEEK,
        content: <Typography>{getDifferenceText(differenceLastLastWeek, true)}</Typography>,
      },
      {
        id: TopSoldProductColumnType.RANKINGVSAREA,
        content: (
          <StyledIndexTypography status={getRankStatus(rankingVsArea)}>{rankingVsArea}</StyledIndexTypography>
        ),
      },
      {
        id: TopSoldProductColumnType.RANKINGVSSALESMARKET,
        content: (
          <StyledIndexTypography status={getRankStatus(rankingVsSalesMarket)}>
            {rankingVsSalesMarket}
          </StyledIndexTypography>
        ),
      },
    ];
  };

  const productSummaryCardOnClick = (cardDetails: SoldProductItemRankedAreaSalesMarket) => {
    setIsSliderOpen(true);
    setSelectedRowData(cardDetails);
  };

  const noDataView = useMemo(() => {
    return !productData || productData.length === 0;
  }, [productData]);

  return (
    <ContainerDrawer
      isOpen={isSliderOpen}
      onClose={() => setIsSliderOpen(false)}
      borderRadius={[2]}
      sliderContent={<ProductSummarySlider data={selectedRowData} />}
      width={'300px'}
    >
      <StoreWidgetContent
        widgetVariant="default"
        title={t(topSoldProductsTranslations.title.key, topSoldProductsTranslations.title.defaultValue)}
        tooltipText={t(topSoldProductsTranslations.tooltip.key, topSoldProductsTranslations.tooltip.defaultValue)}
        isLoading={isLoading}
        isError={isError}
        errorRetryHandler={retry}
        isNoData={!weeklyTopSoldData}
        errorText={t(
          topSoldProductsTranslations.errorText.key,
          topSoldProductsTranslations.errorText.defaultValue,
        )}
        widgetContent={
          <ColumnFlexContainer height="100%">
            {noDataView && <NoDataForPeriodView />}

            {!isMobile && productData && !noDataView && (
              <ColumnFlexContainer padding={[2, 0, 2]} flex={1} data-testid="top-sold-product-table-wrapper">
                <Table
                  data-testid="top-sold-product-table"
                  items={productData}
                  headerItems={headerItems}
                  stickyHeader
                  onRowSelect={(item) => productSummaryCardOnClick(item)}
                  fixedHeight={'300px'}
                  sxProps={{ padding: '8px' }}
                  headerSxProps={{ padding: '8px' }}
                  getRowItems={(item) => {
                    const row = getTableRow(item);
                    return row;
                  }}
                />
              </ColumnFlexContainer>
            )}

            {isMobile && productData && !noDataView && (
              <ColumnFlexContainer
                data-testid="top-sold-product-list"
                gap={[2]}
                margin={[2, 0, 2]}
                overflow="auto"
                height={[75]}
              >
                {productData.map((item) => (
                  <ProductSummaryCard key={item.productId} data={item} onClick={productSummaryCardOnClick} />
                ))}
              </ColumnFlexContainer>
            )}
          </ColumnFlexContainer>
        }
      />
    </ContainerDrawer>
  );
};
